import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, DollarSign, Headphones, ClipboardCheck } from 'lucide-react';
import { formatPrice, TIMEPAYMENT_URL } from '../lib/api';
import { hasPrice, isBuyNow, stockStatus } from '../lib/shop';
import { StockBadge, AddToCartButton, QuoteButton } from './ShopActions';
import { PHONE_TEL, buildContactHref } from './CTASection';

const Qty = ({ qty, setQty }) => (
    <div className="flex items-center border border-neutral-300 bg-white">
        <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-neutral-100" data-testid="buybox-qty-dec"><Minus className="h-4 w-4" /></button>
        <span className="w-10 text-center font-mono" data-testid="buybox-qty">{qty}</span>
        <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-neutral-100" data-testid="buybox-qty-inc"><Plus className="h-4 w-4" /></button>
    </div>
);

const BuyBox = ({ m }) => {
    const [qty, setQty] = useState(1);
    const buy = isBuyNow(m);

    return (
        <div className="mt-6 border border-neutral-200 bg-neutral-50 p-6" data-testid="machine-buybox">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-caps text-neutral-500 text-xs">{buy ? 'Price' : hasPrice(m) ? 'Starting at' : 'Pricing'}</div>
                    <div className="font-display font-black text-4xl text-neutral-900 mt-1" data-testid="buybox-price">
                        {hasPrice(m) ? formatPrice(m.price) : 'Call for current price'}
                    </div>
                </div>
                {buy && <StockBadge m={m} testid="buybox-stock" />}
            </div>
            {buy && stockStatus(m) !== 'in_stock' && (
                <p className="mt-3 text-sm text-neutral-600" data-testid="buybox-backorder-note">You can still order today — we&apos;ll ship as soon as it arrives.</p>
            )}
            {!buy && (
                <p className="mt-3 text-sm text-neutral-600">Machines are quoted with freight, setup, training and financing options for your operation.</p>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
                {buy ? (
                    <>
                        <Qty qty={qty} setQty={setQty} />
                        <AddToCartButton m={m} qty={qty} className="flex-1 px-6 py-3.5" testid="machine-cta-add-to-cart" />
                    </>
                ) : (
                    <QuoteButton m={m} className="flex-1 px-6 py-3.5" testid="machine-cta-quote" />
                )}
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {buy && (
                    <Link to={buildContactHref({ subject: `Question about: ${m.name}`, machine: m.name, interest: 'quote' })}
                        className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:border-neutral-900 px-3 py-3 label-caps text-xs transition-colors" data-testid="machine-cta-question">
                        <ClipboardCheck className="h-4 w-4" /> Ask a question
                    </Link>
                )}
                {m.financing_available && (
                    <a href={TIMEPAYMENT_URL} target="_blank" rel="noreferrer"
                        onClick={() => window.trackConversion && window.trackConversion('finance_click', { location: 'buybox', machine: m.name })}
                        className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:border-neutral-900 px-3 py-3 label-caps text-xs transition-colors" data-testid="machine-cta-finance">
                        <DollarSign className="h-4 w-4" /> Finance it
                    </a>
                )}
                <a href={`tel:${PHONE_TEL}`}
                    onClick={() => window.trackConversion && window.trackConversion('specialist_click', { location: 'buybox', machine: m.name })}
                    className="inline-flex items-center justify-center gap-2 border border-neutral-300 bg-white hover:border-brand-primary hover:text-brand-primary px-3 py-3 label-caps text-xs transition-colors" data-testid="machine-cta-specialist">
                    <Headphones className="h-4 w-4" /> Talk to a specialist
                </a>
            </div>
        </div>
    );
};

export default BuyBox;
